import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import debounce from 'lodash/debounce';
import {
  Form,
  Input,
  Typography,
  Select,
  DatePicker,
  Checkbox,
  Button,
} from 'antd';
// Api
import {
  CREATE_CAMPAIGN_EXAMPLE_FILES,
  UPDATE_BOOK_ME_CAMPAIGN,
} from 'api/campaignV2/mutations';
import { SEARCH_BOOK_ME_CAMPAIGN_STORES } from 'api/campaignV2/queries';
import { GET_BOOK_ME_STORES, GET_STORE_INFO } from 'api/store/queries';
// Helpers
import {
  convertEngagementRateToOption,
  formatDaysToWeeks,
  getEngagementRateValue,
} from 'helpers/campaign';
import {
  getCustomActivity,
  getInPersonAppearanceActivity,
  getSocialActivity,
  getVirtualAppearanceActivity,
} from 'helpers/cbActivity';
import {
  PRODUCT_TYPES,
  uploadImages,
  FOLLOWING_OPTIONS_MIN,
  FOLLOWING_OPTIONS_MAX,
  getLocationsForApi,
} from 'helpers/campaignEditModal';
import { buildAudienceFilters } from 'helpers/audienceFilters';
// Constants
import { dateTimeFormat, onlyDateTimeFormat } from 'constants/global';
// Types
import {
  UpdateBookMeCampaign,
  UpdateBookMeCampaignVariables,
} from 'api/campaignV2/types/UpdateBookMeCampaign';
import {
  AdminStoresOrderBy,
  BookMeActivityInput,
  CampaignActivityCoveredExpense,
  CampaignActivityType,
  CustomActivityInput,
  InPersonActivityInput,
  SocialActivityInput,
  StoreStatus,
  UpdateBookMeCampaignInput,
  UserRole,
  VirtualAppearanceActivityInput,
  SearchBookMeCriteriaInput,
  StoreAthleticLevel,
  StoreGender,
  CampaignLocationInput,
  AudienceGender,
  AudienceAgeRange,
  AudienceEthnicity,
  AudienceInterest,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search_BookMeSearchCriteria,
} from 'api/campaignV2/types/GetCampaignsV2';
import { GetStore, GetStoreVariables } from 'api/store/types/GetStore';
import {
  CreateCampaignExampleFiles,
  CreateCampaignExampleFilesVariables,
} from 'api/campaignV2/types/CreateCampaignExampleFiles';
import {
  GetBookMeStores,
  GetBookMeStoresVariables,
} from 'api/store/types/GetBookMeStores';
import {
  SearchBookMeCampaignStores,
  SearchBookMeCampaignStoresVariables,
} from 'api/campaignV2/types/SearchBookMeCampaignStores';
import { RangePickerProps } from 'antd/lib/date-picker';
// Hooks
import { useGetMyEntitlements } from 'hooks';
// Helpers
import { canEditPremiumFilters } from 'helpers/brands';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
import UploadFilesInput from 'ui/UploadFIlesInput/UploadFilesInput';
// Components
import TalentItem from '../../../CampaignDetails/CampaignDetails/components/TalentItem/TalentItem';
import CampaignCriteria from 'components/common/CampaignEditComponents/CampaignCriteria/CampaignCriteria';
// Style
import styles from './BookMeCampaignsEditModal.module.scss';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

const PAGE_SIZE = 8;

type FormValues = {
  name: string;
  internalName: string;
  dueDate: any;
  applicationDeadlineDays?: number | null;
  applicationDeadlineEnabled?: boolean;
  brandName: string;
  flatFeePerStore: number;
  quantityInstructions?: string | null;
  suggestedCaption?: string | null;
  profileMentions?: string | null;
  hashtags?: string | null;
  physicalProductType?: string | null;
  productName?: string | null;
  productValue?: number | null;
  promoCodes?: string | null;
  promoUrls?: string | null;
  encouraged?: string | null;
  avoid?: string | null;
  scriptUrl?: string | null;
  submissionDue?: any;
  socialDateFrom?: any;
  socialDateTo?: any;
  inPersonFiles?: any;
  virtualFiles?: any;
  customFiles?: any;
  inPersonAppearanceDuration?: string | null;
  inPersonAppearanceDescription?: string | null;
  venueName?: string | null;
  address?: string | null;
  state?: string | null;
  city?: string | null;
  zipCode?: string | null;
  instructions?: string | null;
  virtualAppearanceDuration?: string | null;
  virtualAppearanceDescription?: string | null;
  virtualLocation?: string | null;
  requirements?: string | null;
  customRequirements?: string | null;
  usageRightsText: string | null;
  userRole: UserRole[];
  budgetFrom?: number | null;
  budgetTo?: number | null;
  genders?: StoreGender[] | null;
  maxAge?: number | null;
  minAge?: number | null;
  followingFrom: number | null;
  followingTo: number | null;
  sports: string[];
  locations?: CampaignLocationInput[] | null;
  hometownLocations?: CampaignLocationInput[] | null;
  excludedLocations?: CampaignLocationInput[] | null;
  engagementRate?: { value: number; label: string } | null;
  searchHashtags?:
    | { label: string; value: string; storeCount: number }[]
    | null;
  athleticLevels?: StoreAthleticLevel[] | null;
  audienceGender?: AudienceGender | null;
  audienceAgeRanges?: AudienceAgeRange[] | null;
  audienceEthnicities?: AudienceEthnicity[] | null;
  audienceLocations?: CampaignLocationInput[] | null;
  audienceInterests?: AudienceInterest[] | null;
};

type BookMeCampaignsEditModalProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  onFinish: () => void;
};

const normFile = (e: any) => {
  // AntD expects an array of files
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const BookMeCampaignsEditModal = ({
  record,
  onFinish,
}: BookMeCampaignsEditModalProps): JSX.Element | null => {
  const [form] = Form.useForm();

  const ownerId = record?.agency?.id;
  const { data: entitlementsData } = useGetMyEntitlements({
    ownerId,
    skip: !ownerId,
  });
  const canEditPremiumFiltersValue = canEditPremiumFilters(
    entitlementsData?.getMyEntitlements
  );

  const socialActivity = getSocialActivity(record);
  const virtualAppearanceActivity = getVirtualAppearanceActivity(record);
  const inPersonAppearanceActivity = getInPersonAppearanceActivity(record);
  const customActivity = getCustomActivity(record);

  const search =
    record?.search as GetCampaignsV2_getCampaigns_entities_search_BookMeSearchCriteria;
  const talentId = search?.talentId;

  const [isPhysicalProduct, setIsPhysicalProduct] = useState<boolean>(
    socialActivity?.physicalProduct || false
  );

  const [selectedStoreOption, setSelectedStoreOption] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [isSocial, setIsSocial] = useState<boolean>(!!socialActivity);

  const [isInPerson, setIsInPerson] = useState<boolean>(
    !!inPersonAppearanceActivity
  );

  const [isVirtual, setIsVirtual] = useState<boolean>(
    !!virtualAppearanceActivity
  );

  const [isCustom, setIsCustom] = useState<boolean>(!!customActivity);

  const [usageRights, setUsageRights] = useState<boolean>(
    record?.usageRights || false
  );

  const [inPersonDates, setInPersonDates] = useState<any[]>(
    inPersonAppearanceActivity &&
      'possibleDates' in inPersonAppearanceActivity &&
      Array.isArray(inPersonAppearanceActivity?.possibleDates)
      ? inPersonAppearanceActivity.possibleDates
          .filter(Boolean)
          .map((date) => (date ? moment(date) : null))
      : []
  );

  const [virtualDates, setVirtualDates] = useState<any[]>(
    virtualAppearanceActivity &&
      'possibleDates' in virtualAppearanceActivity &&
      Array.isArray(virtualAppearanceActivity?.possibleDates)
      ? virtualAppearanceActivity.possibleDates
          .filter(Boolean)
          .map((date) => (date ? moment(date) : null))
      : []
  );

  const [coveredExpenses, setCoveredExpenses] = useState<
    CampaignActivityCoveredExpense[]
  >(
    inPersonAppearanceActivity &&
      'coveredExpenses' in inPersonAppearanceActivity &&
      inPersonAppearanceActivity?.coveredExpenses?.length
      ? [...inPersonAppearanceActivity?.coveredExpenses]
      : []
  );

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [currentApplicationDeadlineDays, setCurrentApplicationDeadlineDays] =
    useState<number | null>(record?.applicationDeadlineDays || null);

  // const [applicationDeadlineEnabled, setApplicationDeadlineEnabled] =
  //   useState<boolean>(record?.applicationDeadlineEnabled ?? true);

  const [currentPage, setCurrentPage] = useState<number>(1);

  const [updateCampaign, { loading: loadingUpdateCampaign }] = useMutation<
    UpdateBookMeCampaign,
    UpdateBookMeCampaignVariables
  >(UPDATE_BOOK_ME_CAMPAIGN);

  const [
    createCampaignExampleFiles,
    { loading: loadingCreateCampaignExampleFiles },
  ] = useMutation<
    CreateCampaignExampleFiles,
    CreateCampaignExampleFilesVariables
  >(CREATE_CAMPAIGN_EXAMPLE_FILES);

  const { data: storesData, refetch } = useQuery<
    GetBookMeStores,
    GetBookMeStoresVariables
  >(GET_BOOK_ME_STORES, {
    variables: {
      input: {
        orderBy: AdminStoresOrderBy.storeName,
        status: [StoreStatus.Active],
        isOnboarded: true,
      },
      storeRoles: [
        UserRole.Athlete,
        UserRole.Organization, // this is different from the campaign builder project-it's a requested addition
        UserRole.ContentCreator,
      ],
    },
  });

  const { data: storeInfo } = useQuery<GetStore, GetStoreVariables>(
    GET_STORE_INFO,
    {
      variables: {
        input: {
          id: talentId,
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !talentId,
    }
  );

  const [
    searchBookMeStores,
    { data: searchStoreData, loading: loadingStoreData },
  ] = useLazyQuery<
    SearchBookMeCampaignStores,
    SearchBookMeCampaignStoresVariables
  >(SEARCH_BOOK_ME_CAMPAIGN_STORES);

  const storeName = storeInfo?.store.storeDetails?.storeName;
  const initialHashtags = socialActivity?.hashtags.join(',');
  const initialPromoCodes = socialActivity?.promoCodes?.join(',');
  const initialPromoURL = socialActivity?.promoUrls?.join(',');

  const initialFormValues: FormValues = {
    name: record?.name || '',
    internalName: record?.internalName || '',
    dueDate: record?.dueDate ? moment(record?.dueDate) : null,
    applicationDeadlineDays: record?.applicationDeadlineDays || null,
    applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
    brandName: record?.brandName || '',
    flatFeePerStore: Number(record?.flatFeePerStore),
    quantityInstructions: socialActivity?.quantityInstructions || '',
    suggestedCaption: socialActivity?.suggestedCaption || '',
    profileMentions: socialActivity?.profileMentions || '',
    hashtags: initialHashtags ?? null,
    physicalProductType: socialActivity?.physicalProductType || null,
    productName: socialActivity?.productName || null,
    productValue: socialActivity?.productValue || null,
    promoCodes: initialPromoCodes ?? null,
    promoUrls: initialPromoURL ?? null,
    encouraged: socialActivity?.encouraged,
    avoid: socialActivity?.avoid,
    scriptUrl: socialActivity?.script || null,
    submissionDue: socialActivity?.submissionDue
      ? moment(socialActivity?.submissionDue)
      : null,
    socialDateFrom: socialActivity?.postDueFrom
      ? moment(socialActivity?.postDueFrom)
      : null,
    socialDateTo: socialActivity?.postDueTo
      ? moment(socialActivity?.postDueTo)
      : null,
    inPersonFiles: inPersonAppearanceActivity?.files || [],
    virtualFiles: virtualAppearanceActivity?.files || [],
    customFiles: customActivity?.files || [],
    inPersonAppearanceDuration: inPersonAppearanceActivity?.duration || '',
    inPersonAppearanceDescription:
      inPersonAppearanceActivity?.description || '',
    venueName: inPersonAppearanceActivity?.venueName || '',
    address: inPersonAppearanceActivity?.address || '',
    state: inPersonAppearanceActivity?.state || '',
    city: inPersonAppearanceActivity?.city || '',
    zipCode: inPersonAppearanceActivity?.zip || '',
    instructions: inPersonAppearanceActivity?.instructions || '',
    virtualAppearanceDuration: virtualAppearanceActivity?.duration || '',
    virtualAppearanceDescription: virtualAppearanceActivity?.description || '',
    virtualLocation: virtualAppearanceActivity?.location || '',
    requirements: virtualAppearanceActivity?.instructions || '',
    customRequirements: customActivity?.instructions || '',
    usageRightsText: record?.usageRightsText || null,
    userRole: search?.roles || [],
    budgetFrom: search?.budgetRangeFrom || null,
    budgetTo: search?.budgetRangeTo || null,
    genders: search?.genders || [],
    maxAge: search?.athleteMaxAge || null,
    minAge: search?.athleteMinAge || null,
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find((f) => f.value === search.minFollowers)
        ?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find((f) => f.value === search.maxFollowers)
        ?.value || null,
    sports: search?.sports || [],
    locations: search?.locations || [],
    hometownLocations: search?.hometownLocations || [],
    excludedLocations: search?.excludedLocations || [],
    engagementRate: convertEngagementRateToOption(search?.engagementRateFrom),
    searchHashtags: search?.hashtags
      ? search.hashtags.map((tag, idx) => ({
          label: tag,
          value: String(idx),
          storeCount: 0,
        }))
      : [],
    athleticLevels: search?.athleticLevels || [],
    audienceGender: search?.audienceGender || null,
    audienceAgeRanges: search?.audienceAgeRanges || [],
    audienceEthnicities: search?.audienceEthnicities || [],
    audienceLocations: search?.audienceLocations || [],
    audienceInterests: search?.audienceInterests || [],
  };

  useEffect(() => {
    // updates the talent input with correct data once it's loaded
    // Only set selectedStoreOption if talentId is a valid non-null value
    if (talentId) {
      setSelectedStoreOption({
        value: talentId,
        label: storeName || '',
      });
      form.setFields([
        {
          name: 'talentId',
          errors: [],
          value: storeName,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, talentId]);

  // Set initial form values and trigger filter search when record changes (only reset when editing a different campaign)
  useEffect(() => {
    if (record && search) {
      const initialFormValues: Partial<FormValues> = {
        userRole: search?.roles || [],
        budgetFrom: search?.budgetRangeFrom || null,
        budgetTo: search?.budgetRangeTo || null,
        genders: search?.genders || [],
        maxAge: search?.athleteMaxAge || null,
        minAge: search?.athleteMinAge || null,
        followingFrom:
          FOLLOWING_OPTIONS_MIN.find((f) => f.value === search?.minFollowers)
            ?.value || null,
        followingTo:
          FOLLOWING_OPTIONS_MAX.find((f) => f.value === search?.maxFollowers)
            ?.value || null,
        sports: search?.sports || [],
        locations: search?.locations || [],
        hometownLocations: search?.hometownLocations || [],
        excludedLocations: search?.excludedLocations || [],
        engagementRate: convertEngagementRateToOption(
          search?.engagementRateFrom
        ),
        searchHashtags: search?.hashtags
          ? search.hashtags.map((tag, idx) => ({
              label: tag,
              value: String(idx),
              storeCount: 0,
            }))
          : [],
        athleticLevels: search?.athleticLevels || [],
        audienceGender: search?.audienceGender || null,
        audienceAgeRanges: search?.audienceAgeRanges || [],
        audienceEthnicities: search?.audienceEthnicities || [],
        audienceLocations: search?.audienceLocations || [],
        audienceInterests: search?.audienceInterests || [],
      };

      // Set form values
      form.setFieldsValue({
        name: record?.name || '',
        internalName: record?.internalName || '',
        brandName: record?.brandName || '',
        dueDate: record?.dueDate ? moment(record?.dueDate) : null,
        applicationDeadlineDays: record?.applicationDeadlineDays || null,
        applicationDeadlineEnabled: record?.applicationDeadlineEnabled ?? true,
        flatFeePerStore: Number(record?.flatFeePerStore) || 0,
        usageRightsText: record?.usageRightsText || null,
        ...initialFormValues,
      });

      // Trigger filter search with initial values
      const criteria = buildSearchCriteria(initialFormValues as FormValues);
      handleFilterSearch(criteria);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const handleSelectStore = (value: string, option: any): void => {
    setSelectedStoreOption({ value: option.key, label: option.children });
  };

  const buildSearchCriteria = (
    formValues: FormValues
  ): SearchBookMeCriteriaInput => {
    const searchLocations = getLocationsForApi(formValues.locations || []);
    const searchHometownLocations = getLocationsForApi(
      formValues.hometownLocations || []
    );
    const searchExcludedLocations = getLocationsForApi(
      formValues.excludedLocations || []
    );

    const audienceFilters = buildAudienceFilters({
      audienceGender: formValues.audienceGender || null,
      audienceAgeRanges: formValues.audienceAgeRanges || null,
      audienceEthnicities: formValues.audienceEthnicities || null,
      audienceLocations: formValues.audienceLocations || null,
      audienceInterests: formValues.audienceInterests || null,
      isTikTok: false,
    });

    const criteria: SearchBookMeCriteriaInput = {};

    if (formValues.userRole?.length) {
      criteria.roles = formValues.userRole;
    }

    if (formValues.budgetFrom && formValues.budgetTo) {
      criteria.budgetRange = {
        from: Number(formValues.budgetFrom),
        to: Number(formValues.budgetTo),
      };
    }

    if (formValues.genders?.length) {
      criteria.genders = formValues.genders;
    }

    if (formValues.maxAge) {
      criteria.maxAge = formValues.maxAge;
    }

    if (formValues.minAge) {
      criteria.minAge = formValues.minAge;
    }

    if (formValues.followingFrom && formValues.followingTo) {
      criteria.socialFollowersRange = {
        from: formValues.followingFrom,
        to: formValues.followingTo,
      };
    } else if (formValues.followingFrom) {
      criteria.socialFollowersRange = { from: formValues.followingFrom };
    } else if (formValues.followingTo) {
      criteria.socialFollowersRange = { to: formValues.followingTo };
    }

    if (formValues.sports?.length) {
      criteria.sports = formValues.sports;
    }

    if (searchLocations.length) {
      criteria.locations = searchLocations;
    }

    if (searchHometownLocations.length) {
      criteria.hometownLocations = searchHometownLocations;
    }

    if (searchExcludedLocations.length) {
      criteria.excludedLocations = searchExcludedLocations;
    }

    const engagementRateValue = getEngagementRateValue(
      formValues.engagementRate
    );
    if (engagementRateValue !== null) {
      criteria.engagementRateRange = {
        from: engagementRateValue,
        to: 1,
      };
    }

    if (formValues.searchHashtags?.length) {
      criteria.hashtagInputs = formValues.searchHashtags.map((tag: any) => ({
        name: tag.label.trim(),
      }));
    }

    if (formValues.athleticLevels?.length) {
      criteria.athleticLevels = formValues.athleticLevels;
    }

    if (audienceFilters) {
      criteria.audienceFilters = audienceFilters;
    }

    return criteria;
  };

  const handleFilterSearch = async (criteria: SearchBookMeCriteriaInput) => {
    try {
      await searchBookMeStores({
        variables: {
          input: {
            criteria,
            limit: PAGE_SIZE,
            offset: (currentPage - 1) * PAGE_SIZE,
          },
        },
      });
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign filter error:', { error });
    }
  };

  const handleFilterClick = async () => {
    // Reset to first page when applying new filters
    setCurrentPage(1);
    const formValues = form.getFieldsValue();
    const criteria = buildSearchCriteria(formValues);
    handleFilterSearch(criteria);
  };

  const handleSelectTalent = (talentId: string) => {
    const selectedTalent = searchStoreData?.searchBookMeStores.entities.find(
      (talent: any) => talent.id === talentId
    );
    if (selectedTalent) {
      setSelectedStoreOption({
        value: talentId,
        label:
          selectedTalent.storeDetails?.storeName ||
          `${selectedTalent.firstName} ${selectedTalent.lastName}`,
      });
      form.setFieldsValue({
        talentId:
          selectedTalent.storeDetails?.storeName ||
          `${selectedTalent.firstName} ${selectedTalent.lastName}`,
      });
      successNotification('Store selection applied');
    }
  };

  const talentsData = searchStoreData?.searchBookMeStores?.entities;
  const hasMore =
    (talentsData?.length ?? 0) <
    (searchStoreData?.searchBookMeStores.total ?? 0);
  const showTalentList =
    !!talentsData?.length && talentsData?.length > 0 && !loadingStoreData;
  const totalTalents = Number(searchStoreData?.searchBookMeStores.total);

  const handleSubmit = async () => {
    const {
      name,
      internalName,
      dueDate,
      brandName,
      quantityInstructions,
      suggestedCaption,
      profileMentions,
      hashtags,
      physicalProductType,
      productName,
      productValue,
      promoCodes,
      promoUrls,
      encouraged,
      avoid,
      scriptUrl,
      submissionDue,
      socialDateFrom,
      socialDateTo,
      inPersonAppearanceDuration,
      inPersonAppearanceDescription,
      venueName,
      address,
      state,
      city,
      zipCode,
      instructions,
      virtualAppearanceDuration,
      virtualAppearanceDescription,
      virtualLocation,
      requirements,
      usageRightsText,
      customRequirements,
      applicationDeadlineDays,
      // applicationDeadlineEnabled,
      userRole,
      budgetFrom,
      budgetTo,
      genders,
      maxAge,
      minAge,
      followingFrom,
      followingTo,
      sports,
      locations,
      hometownLocations,
      excludedLocations,
      engagementRate,
      searchHashtags,
      athleticLevels,
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceLocations,
      audienceInterests,
      flatFeePerStore,
    } = form.getFieldsValue();

    const searchLocations = getLocationsForApi(locations || []);
    const searchAudienceLocations = getLocationsForApi(audienceLocations || []);
    const audienceFilters = buildAudienceFilters({
      audienceGender,
      audienceAgeRanges,
      audienceEthnicities,
      audienceLocations: searchAudienceLocations,
      audienceInterests,
      isTikTok: false,
    });

    const criteria: SearchBookMeCriteriaInput = {
      ...(userRole?.length && { roles: userRole }),
      ...(budgetFrom &&
        budgetTo && {
          budgetRange: { from: Number(budgetFrom), to: Number(budgetTo) },
        }),
      ...(genders?.length && { genders }),
      ...(maxAge && { maxAge: Number(maxAge) }),
      ...(minAge && { minAge: Number(minAge) }),
      ...(followingFrom && {
        socialFollowersRange: { from: Number(followingFrom) },
      }),
      ...(followingTo && {
        socialFollowersRange: { to: Number(followingTo) },
      }),
      ...(followingTo &&
        followingFrom && {
          socialFollowersRange: {
            from: Number(followingFrom),
            to: Number(followingTo),
          },
        }),
      ...(sports?.length && { sports }),
      ...(searchLocations.length && { locations: searchLocations }),
      ...(hometownLocations?.length && {
        hometownLocations: getLocationsForApi(hometownLocations),
      }),
      ...(excludedLocations?.length && {
        excludedLocations: getLocationsForApi(excludedLocations),
      }),
      ...(() => {
        const engagementRateValue = getEngagementRateValue(engagementRate);
        return engagementRateValue !== null
          ? {
              engagementRateRange: {
                from: engagementRateValue,
                to: 1,
              },
            }
          : {};
      })(),
      ...(searchHashtags?.length && {
        hashtagInputs: searchHashtags.map((tag: any) => ({
          name: tag.label.trim(),
        })),
      }),
      ...(athleticLevels?.length && {
        athleticLevels: athleticLevels,
      }),
      ...(audienceFilters && { audienceFilters }),
    };

    const inPersonFiles = form.getFieldValue('inPersonFiles');
    const virtualFiles = form.getFieldValue('virtualFiles');
    const customFiles = form.getFieldValue('customFiles');
    const activities: BookMeActivityInput[] = [];

    // social has no files so it's added to activities(if there is a Social), while others might need their images uploaded first
    if (isSocial) {
      const social: SocialActivityInput = {
        ...(avoid && { avoid }),
        brandName,
        ...(encouraged && { encouraged }),
        ...(hashtags && {
          hashtags:
            hashtags?.split(',')?.map((tag: string) => tag.trim()) || hashtags,
        }),
        physicalProduct: isPhysicalProduct,
        ...(isPhysicalProduct && { physicalProductType }),
        ...(isPhysicalProduct && { productName }),
        ...(isPhysicalProduct && { productValue: Number(productValue) }),
        ...(profileMentions && { profileMentions }),
        ...(promoCodes && {
          promoCodes:
            promoCodes?.split(',')?.map((tag: string) => tag.trim()) ||
            promoCodes,
        }),
        ...(promoUrls && {
          promoUrls:
            promoUrls?.split(',')?.map((tag: string) => tag.trim()) ||
            promoUrls,
        }),
        ...(quantityInstructions && { quantityInstructions }),
        ...(scriptUrl && { script: scriptUrl }),
        ...(suggestedCaption && { suggestedCaption }),
        ...(submissionDue && { submissionDue }),
        ...(socialDateFrom && { postDueFrom: socialDateFrom }),
        ...(socialDateTo && { postDueTo: socialDateTo }),
      };
      activities.push({ social, type: CampaignActivityType.Social });
    }

    const inPersonFilesToUpload: File[] = [];
    const virtualFilesToUpload: File[] = [];
    const customFilesToUpload: File[] = [];

    const inPersonUploadUrls: string[] = [];
    const virtualUploadUrls: string[] = [];
    const customUploadUrls: string[] = [];

    inPersonFiles.forEach((file: any) => {
      if (file.key) {
        inPersonUploadUrls.push(file.key);
      } else if (file instanceof File) {
        inPersonFilesToUpload.push(file);
      }
    });

    virtualFiles.forEach((file: any) => {
      if (file.key) {
        virtualUploadUrls.push(file.key);
      } else if (file instanceof File) {
        virtualFilesToUpload.push(file);
      }
    });

    customFiles.forEach((file: any) => {
      if (file.key) {
        customUploadUrls.push(file.key);
      } else if (file instanceof File) {
        customFilesToUpload.push(file);
      }
    });

    const hasImagesToUpload =
      !!inPersonFilesToUpload.length ||
      !!virtualFilesToUpload.length ||
      !!customFilesToUpload.length;

    try {
      if (hasImagesToUpload) {
        setIsUploading(true);
        if (inPersonFilesToUpload.length) {
          const { data: inPersonFilesData } = await createCampaignExampleFiles({
            variables: {
              input: {
                files: inPersonFilesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          });
          if (inPersonFilesData?.createCampaignExampleFiles?.length) {
            const urls = await uploadImages(
              inPersonFilesData.createCampaignExampleFiles,
              inPersonFilesToUpload
            );
            inPersonUploadUrls.push(...urls);
          }
        }

        if (virtualFilesToUpload.length) {
          const { data: virtualFilesData } = await createCampaignExampleFiles({
            variables: {
              input: {
                files: virtualFilesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          });
          if (virtualFilesData?.createCampaignExampleFiles?.length) {
            const urls = await uploadImages(
              virtualFilesData.createCampaignExampleFiles,
              virtualFilesToUpload
            );
            virtualUploadUrls.push(...urls);
          }
        }

        if (customFilesToUpload.length) {
          const { data: customFilesData } = await createCampaignExampleFiles({
            variables: {
              input: {
                files: customFilesToUpload.map((file) => ({
                  name: file.name,
                })),
              },
            },
          });
          if (customFilesData?.createCampaignExampleFiles?.length) {
            const urls = await uploadImages(
              customFilesData.createCampaignExampleFiles,
              customFilesToUpload
            );
            customUploadUrls.push(...urls);
          }
        }

        setIsUploading(false);
      }

      if (isInPerson) {
        const validInPersonDates = inPersonDates.filter(
          (date) => date && date.isValid()
        );

        const inPersonAppearance: InPersonActivityInput = {
          ...(address && { address }),
          ...(city && { city }),
          ...(coveredExpenses && { coveredExpenses }),
          ...(inPersonAppearanceDescription && {
            description: inPersonAppearanceDescription,
          }),
          ...(inPersonAppearanceDuration && {
            duration: inPersonAppearanceDuration,
          }),
          ...(instructions && { instructions }),
          ...(validInPersonDates.length && {
            possibleDates: validInPersonDates.map((date) => date.toISOString()),
          }),
          ...(state && { state }),
          ...(venueName && { venueName }),
          ...(zipCode && { zip: zipCode }),
          ...(inPersonUploadUrls.length && {
            files: inPersonUploadUrls.map((url) => ({ key: url })),
          }),
        };
        activities.push({
          inPersonAppearance,
          type: CampaignActivityType.InPersonAppearance,
        });
      }

      if (isVirtual) {
        const validDates = virtualDates.filter(
          (date) => date && date.isValid()
        );

        const virtualAppearance: VirtualAppearanceActivityInput = {
          ...(virtualAppearanceDescription && {
            description: virtualAppearanceDescription,
          }),
          ...(virtualAppearanceDuration && {
            duration: virtualAppearanceDuration,
          }),
          ...(virtualLocation && { location: virtualLocation }),
          ...(requirements && { instructions: requirements }),
          ...(validDates.length && {
            possibleDates: validDates.map((date) => date.toISOString()),
          }),
          ...(virtualUploadUrls.length && {
            files: virtualUploadUrls.map((url) => ({ key: url })),
          }),
        };
        activities.push({
          virtualAppearance,
          type: CampaignActivityType.VirtualAppearance,
        });
      }

      if (isCustom) {
        const custom: CustomActivityInput = {
          ...(customRequirements && { instructions: customRequirements }),
          ...(customUploadUrls.length && {
            files: customUploadUrls.map((url) => ({ key: url })),
          }),
        };
        activities.push({
          custom,
          type: CampaignActivityType.Custom,
        });
      }

      const updateCampaignInputVariables: UpdateBookMeCampaignInput = {
        id: record?.id || '',
        lockEnabled: true,
        // Only include talentId if there's a valid selected value to avoid sending "null" or empty string
        ...(selectedStoreOption?.value && {
          talentId: selectedStoreOption.value,
        }),
        ...(activities?.length && { activities }),
        ...(brandName && { brandName }),
        ...(dueDate && { dueDate }),
        ...(name && { name }),
        internalName: internalName || name,
        ...(usageRights && { usageRights }),
        ...(usageRightsText && { usageRightsText }),
        ...(applicationDeadlineDays && { applicationDeadlineDays }),
        ...(Object.keys(criteria).length > 0 && { criteria }),
        ...(flatFeePerStore && { flatFeePerStore: Number(flatFeePerStore) }),
        // ...(applicationDeadlineEnabled !== undefined && {
        //   applicationDeadlineEnabled,
        // }),
      };

      const { data } = await updateCampaign({
        variables: {
          input: updateCampaignInputVariables,
        },
      });
      if (data?.updateBookMeCampaign) {
        successNotification('The campaign was successfully updated');
        onFinish();
      }
    } catch (error) {
      errorNotification((error as Error)?.message);
      console.error('Campaign update error:', { error });
    }
  };

  const handleIsPhysicalProductChange = () => {
    if (isPhysicalProduct) {
      form.setFieldsValue({
        physicalProductType: null,
      });
    }

    setIsPhysicalProduct(!isPhysicalProduct);
  };

  const handleChangeIsInPerson = () => {
    setIsVirtual(false);
    setIsInPerson(!isInPerson);
    form.setFieldsValue({ isVirtual: false });
  };

  const handleChangeIsVirtual = () => {
    setIsInPerson(false);
    setIsVirtual(!isVirtual);
    form.setFieldsValue({ isInPerson: false });
  };

  const handleInPersonDateChange = (value: any, index: number) => {
    const newDateTimes = [...inPersonDates];
    newDateTimes[index] = value ? moment(value) : null;
    setInPersonDates(newDateTimes);
  };

  const handleVirtualDateChange = (value: any, index: number) => {
    const newDateTimes = [...virtualDates];
    newDateTimes[index] = value ? moment(value) : null;
    setVirtualDates(newDateTimes);
  };

  const handleDeleteInPersonDate = (index: number) => {
    setInPersonDates(inPersonDates.filter((_, counter) => counter !== index));
  };

  const handleDeleteVirtualDate = (index: number) => {
    setVirtualDates(virtualDates.filter((_, counter) => counter !== index));
  };

  const handleAddInPersonDate = () => {
    setInPersonDates([...inPersonDates, null]);
  };

  const handleAddVirtualDate = () => setVirtualDates([...virtualDates, null]);

  const handleUsageRightsChange = () => {
    if (usageRights) {
      form.setFieldsValue({
        usageRights: null,
      });
    }

    setUsageRights(!usageRights);
  };

  // Update currentApplicationDeadlineDays when form changes
  const handleApplicationDeadlineDaysChange = (value: number | null) => {
    setCurrentApplicationDeadlineDays(
      value || record?.applicationDeadlineDays || null
    );
  };

  // const handleApplicationDeadlineEnabledChange = (checked: boolean) => {
  //   setApplicationDeadlineEnabled(checked);
  // };

  const handleChangeCoveredExpense = (
    expense: CampaignActivityCoveredExpense
  ) => {
    setCoveredExpenses((prevCoveredExpenses) =>
      prevCoveredExpenses.includes(expense)
        ? prevCoveredExpenses.filter((e) => e !== expense)
        : [...prevCoveredExpenses, expense]
    );
  };

  const handleOnSearchStores = useMemo(() => {
    const loadOptions = (storeName: string) => {
      refetch({
        storeRoles: [
          UserRole.Athlete,
          UserRole.Organization,
          UserRole.ContentCreator,
        ],
        input: {
          storeName,
          orderBy: AdminStoresOrderBy.storeName,
          status: [StoreStatus.Active],
        },
      });
    };

    return debounce(loadOptions, 400);
  }, [refetch]);

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    // allow days 2 weeks from today
    return current && current < moment().add('2', 'weeks');
  };

  // find the talent for display in storesData by comparing the id
  // if it's initial load, we don't have storesData so use storeInfo(it's fetched for that talent's Id)
  // also check search results data when talent is selected from filtered list
  const selectedStore =
    storesData?.adminStores.entities.find(
      (store) => store.id === selectedStoreOption?.value
    ) ||
    searchStoreData?.searchBookMeStores.entities.find(
      (store) => store.id === selectedStoreOption?.value
    ) ||
    storeInfo?.store;

  const submitLoading =
    isUploading ||
    loadingUpdateCampaign ||
    loadingCreateCampaignExampleFiles ||
    loadingStoreData;

  if (!record) return null;

  return (
    <div className={styles.root}>
      <Form
        name="EditCampaignForm"
        aria-label="Update Social Campaign"
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={initialFormValues}
      >
        <div className={styles.columnWrapper}>
          <div className={styles.rowWrapper}>
            <Form.Item
              name="talentId"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              label="Select talent"
            >
              <Select
                id="talentId"
                placeholder="Select talent"
                filterOption={(inputValue, option) =>
                  option &&
                  typeof option.children === 'string' &&
                  option.children
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
                onSearch={handleOnSearchStores}
                onSelect={handleSelectStore}
                showSearch
              >
                {storesData?.adminStores.entities.map((item) => {
                  return (
                    <Option value={`${item.id}`} key={item.id}>
                      {item.storeDetails?.storeName}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </div>

          {selectedStore ? (
            <div className={styles.talent}>
              <TalentItem talent={selectedStore} isVertical />
            </div>
          ) : null}

          <CampaignCriteria
            form={form}
            initialFormValues={initialFormValues}
            record={record}
            isInstagram={false}
            setIsInstagram={() => {
              /* Book Me doesn't use Instagram */
            }}
            isTwitter={false}
            setIsTwitter={() => {
              /* Book Me doesn't use Twitter */
            }}
            setSelectedTasks={() => {
              /* Book Me doesn't use tasks */
            }}
            deliverablesChecklist={[]}
            setDeliverablesChecklist={() => {
              /* Book Me doesn't use deliverables */
            }}
            handleFilterClick={handleFilterClick}
            loadingStoreData={loadingStoreData}
            talentsData={talentsData}
            hasMore={hasMore}
            totalTalents={totalTalents}
            showTalentList={showTalentList}
            showPlatformSelection={false}
            showAthleticLevel={true}
            onSelectTalent={handleSelectTalent}
            totalFollowers={0} // Book Me campaigns don't use total followers
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            showPagination={true}
            selectedTalentId={selectedStoreOption?.value}
            canEditPremiumFilters={canEditPremiumFiltersValue}
          />

          <div className={styles.heading}>Campaign Details</div>

          <div className={styles.threeItemsRowWrapper}>
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              label={<Text>External Campaign Name</Text>}
            >
              <Input
                type="name"
                placeholder="Enter External Campaign Name"
                aria-label="External Campaign Name"
              />
            </Form.Item>
            <Form.Item
              name="internalName"
              label={<Text>Internal Campaign Name</Text>}
            >
              <Input
                placeholder="Enter Internal Campaign Name"
                aria-label="Internal Campaign Name"
              />
            </Form.Item>

            {record?.dueDate && (
              <Form.Item
                name="dueDate"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                  {
                    validator: (_, value) => {
                      if (value && moment().add(2, 'weeks').isAfter(value)) {
                        return Promise.reject(
                          new Error(
                            'Application deadline must be at least 2 weeks in the future.'
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                label={<Text>Application deadline date</Text>}
                style={{ marginBottom: '4px' }}
              >
                <DatePicker
                  name="dueDate"
                  format={onlyDateTimeFormat}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            )}

            <Form.Item
              name="brandName"
              rules={[
                {
                  required: true,
                  message: 'This field is required',
                },
              ]}
              label={<Text>Brand name</Text>}
            >
              <Input
                type="brandName"
                placeholder="Enter Brand name"
                aria-label="Brand name"
              />
            </Form.Item>
          </div>

          <div className={styles.deadlineWrapper}>
            {/* Maybe it would be added later, so just commented out for now */}
            {/* {record?.applicationDeadlineDays && (
              <Form.Item
                name="applicationDeadlineEnabled"
                label={<Text>Deadline Enabled</Text>}
                valuePropName="checked"
                style={{ marginBottom: '4px' }}
              >
                <Checkbox
                  checked={applicationDeadlineEnabled}
                  onChange={(e) =>
                    handleApplicationDeadlineEnabledChange(e.target.checked)
                  }
                />
              </Form.Item>
            )} */}
            {!record?.dueDate && (
              <div className={styles.deadlinesInput}>
                <Form.Item
                  name="applicationDeadlineDays"
                  getValueFromEvent={(e) => e.target.valueAsNumber || null}
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                    {
                      validator: (_, value) => {
                        if (value && (value < 1 || value > 365)) {
                          return Promise.reject(
                            new Error(
                              'Application deadline must be between 1 and 365 days.'
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  label={<Text>Application deadline (days)</Text>}
                  style={{ marginBottom: '4px' }}
                >
                  <Input
                    type="number"
                    step="1"
                    placeholder="Enter number of days"
                    min={1}
                    max={365}
                    aria-label="Application deadline days"
                    onChange={(e) =>
                      handleApplicationDeadlineDaysChange(
                        e.target.valueAsNumber || null
                      )
                    }
                    // disabled={!applicationDeadlineEnabled}
                  />
                </Form.Item>
                {currentApplicationDeadlineDays && (
                  <div className={styles.deadlineText}>
                    {formatDaysToWeeks(currentApplicationDeadlineDays)} deadline
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.rowWrapper}>
            <Form.Item
              name="usageRights"
              label={<Text>Usage Rights</Text>}
              valuePropName="checked"
              initialValue={usageRights}
            >
              <Checkbox
                value={usageRights}
                onChange={handleUsageRightsChange}
              />
            </Form.Item>

            <Form.Item
              name="usageRightsText"
              rules={[
                {
                  required: usageRights,
                  message: 'This field is required',
                },
              ]}
              label={<Text>Usage rights text</Text>}
            >
              <TextArea
                rows={6}
                id="usageRights"
                placeholder="Usage rights text"
                aria-label="Usage rights text"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="flatFeePerStore"
            rules={[
              {
                required: true,
                message: 'This field is required',
              },
            ]}
            label={<Text>Offer Amount</Text>}
          >
            <Input
              type="number"
              placeholder="Choose your payment method and specify the amount you're offering."
              aria-label="Your Offer"
            />
          </Form.Item>
        </div>

        <div className={styles.columnWrapper}>
          <Form.Item
            name="isSocial"
            label={<Text>Social Post</Text>}
            valuePropName="checked"
            initialValue={isSocial}
          >
            <Checkbox
              value={isSocial}
              onChange={() => {
                setIsSocial(!isSocial);
              }}
            />
          </Form.Item>
          {isSocial && (
            <div className={styles.columnWrapper}>
              <div className={styles.heading}>Social Post</div>

              <Form.Item
                name="quantityInstructions"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Social Platform Post Type and Quantity</Text>}
              >
                <Input
                  type="quantityInstructions"
                  placeholder="Enter Social Platform Post Type and Quantity"
                  aria-label="Social Platform Post Type"
                />
              </Form.Item>
              <Form.Item
                name="suggestedCaption"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Suggested Post Caption</Text>}
              >
                <Input
                  type="suggestedCaption"
                  placeholder="Enter Suggested Post Caption"
                  aria-label="Suggested Post Caption"
                />
              </Form.Item>
              <Form.Item
                name="profileMentions"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Profiles to Tag</Text>}
              >
                <Input
                  type="profileMentions"
                  placeholder="Enter Profiles to Tag"
                  aria-label="Profiles to Tag"
                />
              </Form.Item>

              <Form.Item
                name="hashtags"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label="Hashtags To Use"
              >
                <Input
                  type="hashtags"
                  placeholder="Enter hashtags to use"
                  aria-label="hashtags"
                />
              </Form.Item>

              <div className={styles.rowWrapper}>
                <Form.Item
                  name="physicalProduct"
                  label={<Text>Physical Products</Text>}
                  valuePropName="checked"
                  initialValue={isPhysicalProduct}
                >
                  <Checkbox
                    value={isPhysicalProduct}
                    onChange={handleIsPhysicalProductChange}
                  />
                </Form.Item>
                <Form.Item
                  name="physicalProductType"
                  rules={[
                    {
                      required: isPhysicalProduct,
                      message: 'This field is required',
                    },
                  ]}
                  label={<Text>Product Shipping Category</Text>}
                >
                  <Select
                    id="physicalProductType"
                    placeholder="Select"
                    disabled={!isPhysicalProduct}
                    filterOption={(inputValue, option) =>
                      option?.props.children
                        .toString()
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                  >
                    {PRODUCT_TYPES.map((item) => {
                      return (
                        <Option value={`${item.value}`} key={item.label}>
                          {item.label}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </div>

              {isPhysicalProduct && (
                <div className={styles.rowWrapper}>
                  <Form.Item
                    name="productName"
                    rules={
                      isPhysicalProduct
                        ? [
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]
                        : []
                    }
                    label={<Text>Product Name</Text>}
                    initialValue={socialActivity?.productName}
                  >
                    <Input
                      type="text"
                      placeholder="Enter Product Name"
                      aria-label="Product Name"
                    />
                  </Form.Item>

                  <Form.Item
                    name="productValue"
                    rules={
                      isPhysicalProduct
                        ? [
                            {
                              required: true,
                              message: 'This field is required',
                            },
                          ]
                        : []
                    }
                    label={<Text>Product Value</Text>}
                    initialValue={socialActivity?.productValue}
                  >
                    <Input
                      type="number"
                      placeholder="Enter Product Value"
                      aria-label="Product Value"
                    />
                  </Form.Item>
                </div>
              )}

              <Form.Item
                name="promoCodes"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Promo Code</Text>}
              >
                <Input
                  type="text"
                  placeholder="Enter Promo Codes"
                  aria-label="Promo Codes"
                />
              </Form.Item>

              <Form.Item
                name="promoUrls"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Promo URL</Text>}
              >
                <Input
                  type="text"
                  placeholder="Enter Promo URL"
                  aria-label="Promo URL"
                />
              </Form.Item>

              <Form.Item
                name="encouraged"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Encouraged</Text>}
              >
                <Input
                  type="text"
                  placeholder="Enter encouraged"
                  aria-label="Encouraged"
                />
              </Form.Item>

              <Form.Item
                name="avoid"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Please Avoid</Text>}
              >
                <Input
                  type="text"
                  placeholder="Enter please avoid"
                  aria-label="Avoid"
                />
              </Form.Item>

              <Form.Item
                name="scriptUrl"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Script URL</Text>}
              >
                <Input
                  type="text"
                  placeholder="Enter script URL"
                  aria-label="Script"
                />
              </Form.Item>

              <Form.Item
                name="submissionDue"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Application deadline date</Text>}
              >
                <DatePicker name="submissionDue" format={onlyDateTimeFormat} />
              </Form.Item>

              <Form.Item
                name="socialDateFrom"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Post Date From</Text>}
              >
                <DatePicker name="socialDateFrom" format={onlyDateTimeFormat} />
              </Form.Item>
              <Form.Item
                name="socialDateTo"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Post Date To</Text>}
              >
                <DatePicker name="socialDateTo" format={onlyDateTimeFormat} />
              </Form.Item>
            </div>
          )}
        </div>

        <div className={styles.columnWrapper}>
          <div className={styles.rowWrapper}>
            <Form.Item
              name="isInPerson"
              label={<Text>In Person Appearance</Text>}
              valuePropName="checked"
              initialValue={isInPerson}
            >
              <Checkbox value={isInPerson} onChange={handleChangeIsInPerson} />
            </Form.Item>

            <Form.Item
              name="isVirtual"
              label={<Text>Virtual Appearance</Text>}
              valuePropName="checked"
              initialValue={isVirtual}
            >
              <Checkbox value={isVirtual} onChange={handleChangeIsVirtual} />
            </Form.Item>
          </div>

          {isInPerson && (
            <div className={styles.columnWrapper}>
              <div className={styles.heading}>In Person Appearance</div>

              <div className={styles.dateManager}>
                {inPersonDates.map((date, index) => {
                  return (
                    <div key={index} className={styles.dateRow}>
                      <div className={styles.datePickerWrapper}>
                        <DatePicker
                          format={dateTimeFormat}
                          value={date}
                          onChange={(value) =>
                            handleInPersonDateChange(value, index)
                          }
                          showTime
                        />
                      </div>

                      {index > 0 && (
                        <Button
                          className={styles.deleteDateButton}
                          type="primary"
                          danger
                          onClick={() => handleDeleteInPersonDate(index)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  );
                })}
                <div className={styles.addDateButtonWrapper}>
                  <Button type="primary" onClick={handleAddInPersonDate}>
                    Add another date/time option
                  </Button>
                </div>
              </div>
              <Form.Item
                name="inPersonAppearanceDuration"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Appearance Duration</Text>}
              >
                <Input
                  type="inPersonAppearanceDuration"
                  placeholder="e.g., 2 hours"
                  aria-label="Appearance Duration"
                />
              </Form.Item>
              <Form.Item
                name="inPersonAppearanceDescription"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Appearance Description</Text>}
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="inPersonAppearanceDescription"
                  placeholder="Write Description"
                />
              </Form.Item>
              <Form.Item
                name="venueName"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Venue Name</Text>}
              >
                <Input
                  type="venueName"
                  placeholder="Enter event/venue name"
                  aria-label="Venue Name"
                />
              </Form.Item>
              <Form.Item
                name="address"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Address</Text>}
              >
                <Input
                  type="address"
                  placeholder="Enter address"
                  aria-label="Address"
                />
              </Form.Item>
              <Form.Item
                name="state"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>State</Text>}
              >
                <Input
                  type="state"
                  placeholder="Enter state"
                  aria-label="State"
                />
              </Form.Item>
              <Form.Item
                name="city"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>City</Text>}
              >
                <Input type="city" placeholder="Enter city" aria-label="City" />
              </Form.Item>
              <Form.Item
                name="zipCode"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Zip Code</Text>}
              >
                <Input
                  type="zipCode"
                  placeholder="Enter zip code"
                  aria-label="Zip Code"
                />
              </Form.Item>

              <div className={styles.noMarginHeading}>
                Travel & Accommodations
              </div>
              <Text>
                {`Specify any travel or accommodations you'll cover. (if needed)`}
              </Text>
              <div className={styles.expenseWrapper}>
                {Object.keys(CampaignActivityCoveredExpense).map((expense) => (
                  <div key={expense}>
                    <Text>{expense}</Text>
                    <Checkbox
                      className={styles.expenseCheckbox}
                      type="checkbox"
                      checked={coveredExpenses.some((item) => item === expense)}
                      onChange={() =>
                        handleChangeCoveredExpense(
                          expense as CampaignActivityCoveredExpense
                        )
                      }
                    />
                  </div>
                ))}
              </div>

              <Form.Item
                name="instructions"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Special Instructions</Text>}
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="instructions"
                  placeholder="Special instructions or additional information (e.g., attire, required wifi, required video quality, specific devices needed, topics of conversation, etc...)"
                />
              </Form.Item>

              <div className={styles.uploadRowWrapper}>
                {/* <UploadMultipleImages
                  images={inPersonImages}
                  setImages={handleSetInPersonImages}
                  isMainImage={null}
                /> */}
                <Form.Item getValueFromEvent={normFile} shouldUpdate>
                  {(form) => {
                    return (
                      <UploadFilesInput
                        title="Upload In Person Files"
                        value={form.getFieldValue('inPersonFiles') || []}
                        onChange={(newValue) =>
                          form.setFieldsValue({ inPersonFiles: newValue })
                        }
                        acceptFileTypes={[
                          'image/png',
                          'image/jpeg',
                          'application/pdf',
                          'video/mp4',
                          'video/mov',
                          'video/avi',
                          'video/wmv',
                          'video/flv',
                          'video/mpeg',
                          'video/mpg',
                        ]}
                      />
                    );
                  }}
                </Form.Item>
              </div>
            </div>
          )}
          {isVirtual && (
            <div className={styles.columnWrapper}>
              <div className={styles.heading}>Virtual Appearance</div>

              <div className={styles.heading}>Appearance Date & Time</div>
              <Text>
                {`Choose the date and time for the talent's appearance.`}
              </Text>

              <div className={styles.dateManager}>
                {virtualDates.map((date, index) => (
                  <div key={index} className={styles.dateRow}>
                    <div className={styles.datePickerWrapper}>
                      <Text>Date</Text>
                      <DatePicker
                        format={dateTimeFormat}
                        value={date}
                        onChange={(value) =>
                          handleVirtualDateChange(value, index)
                        }
                        showTime
                      />
                    </div>

                    {index > 0 && (
                      <Button
                        className={styles.deleteDateButton}
                        type="primary"
                        danger
                        onClick={() => handleDeleteVirtualDate(index)}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                ))}
                <div className={styles.addDateButtonWrapper}>
                  <Button type="primary" onClick={handleAddVirtualDate}>
                    Add another date/time option
                  </Button>
                </div>
              </div>
              <Form.Item
                name="virtualAppearanceDuration"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={<Text>Appearance Duration</Text>}
              >
                <Input
                  type="virtualAppearanceDuration"
                  placeholder="e.g., 2 hours"
                  aria-label="Appearance Duration"
                />
              </Form.Item>

              <div className={styles.heading}>Appearance Description</div>

              <Form.Item
                name="virtualAppearanceDescription"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={
                  <Text>{`Detailed description of the event and talent's role at the
                event.`}</Text>
                }
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="virtualAppearanceDescription"
                  placeholder="Write Description"
                />
              </Form.Item>

              <div className={styles.heading}>Appearance Location</div>
              <Form.Item
                name="virtualLocation"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={
                  <Text>
                    What platform will the talent be using to virtually join?
                  </Text>
                }
              >
                <Input
                  type="virtualLocation"
                  placeholder="E.g. Zoom, Phone Call, IG LIVE"
                  aria-label="Appearance Location"
                />
              </Form.Item>

              <div className={styles.heading}>Appearance Requirements</div>
              <Form.Item
                name="requirements"
                rules={[
                  {
                    required: true,
                    message: 'This field is required',
                  },
                ]}
                label={
                  <Text>
                    Upload examples and provide special instructions for the
                    appearance.
                  </Text>
                }
              >
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="requirements"
                  placeholder="Special instructions or additional information (e.g., attire, required wifi, required video quality, specific devices needed, topics of conversation, etc...)"
                />
              </Form.Item>
              <div className={styles.uploadRowWrapper}>
                <Form.Item getValueFromEvent={normFile} shouldUpdate>
                  {(form) => {
                    return (
                      <UploadFilesInput
                        title="Upload Virtual Files"
                        value={form.getFieldValue('virtualFiles') || []}
                        onChange={(newValue) =>
                          form.setFieldsValue({ virtualFiles: newValue })
                        }
                        acceptFileTypes={[
                          'image/png',
                          'image/jpeg',
                          'application/pdf',
                          'video/mp4',
                          'video/mov',
                          'video/avi',
                          'video/wmv',
                          'video/flv',
                          'video/mpeg',
                          'video/mpg',
                        ]}
                      />
                    );
                  }}
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div className={styles.columnWrapper}>
          <div className={styles.rowWrapper}>
            <Form.Item
              name="isCustom"
              label={<Text>Custom</Text>}
              valuePropName="checked"
              initialValue={isCustom}
            >
              <Checkbox
                value={isCustom}
                onChange={() => {
                  setIsCustom(!isCustom);
                }}
              />
            </Form.Item>
          </div>

          {isCustom && (
            <div className={styles.columnWrapper}>
              <div className={styles.heading}>Custom</div>
              <div className={styles.heading}>
                Details & Talent Requirements
              </div>
              <Text>
                Upload examples, briefs, or other files and provide specific
                instructions for the booking.
              </Text>
              <Form.Item name="customRequirements">
                <TextArea
                  autoSize={{ minRows: 3 }}
                  name="customRequirements"
                  placeholder="Please be as specific as possible, including dates, times, exact requirements of the talent and what is being requested of them.
                  E.g. I want to create a long-term partnership that spans over 12 months where you’ll be doing x,y,z, I want to do a video/photo shoot on these dates, I’d like you to make a video for our brand to promote to clients with, etc..."
                />
              </Form.Item>

              <div className={styles.heading}>Examples and Uploads</div>
              <Text>
                Upload files (examples, brand guidelines, campaign/content
                brief, etc...)
              </Text>
              {/* <div className={styles.rowWrapper}>
                <UploadMultipleImages
                  images={customImages}
                  setImages={handleSetCustomImages}
                  isMainImage={null}
                />
              </div> */}
              <div className={styles.rowWrapper}>
                <Form.Item getValueFromEvent={normFile} shouldUpdate>
                  {(form) => {
                    return (
                      <UploadFilesInput
                        title="Upload Example Files"
                        value={form.getFieldValue('customFiles') || []}
                        onChange={(newValue) =>
                          form.setFieldsValue({ customFiles: newValue })
                        }
                        acceptFileTypes={[
                          'image/png',
                          'image/jpeg',
                          'application/pdf',
                          'video/mp4',
                          'video/mov',
                          'video/avi',
                          'video/wmv',
                          'video/flv',
                          'video/mpeg',
                          'video/mpg',
                        ]}
                      />
                    );
                  }}
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div className={styles.submitButtonWrapper}>
          <Button
            style={{ marginRight: 8 }}
            type="primary"
            loading={submitLoading}
            onClick={handleSubmit}
          >
            Save as Draft
          </Button>

          <Button type="primary" htmlType="submit" loading={submitLoading}>
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BookMeCampaignsEditModal;
